<template>
  <span
    ref="input"
    role="spinbutton"
    :contenteditable="!disabled"
    :data-segment="segment.type"
    :data-placeholder="segment.value === null"
    :tabindex="disabled ? -1 : 0"
    :aria-label="segment.type"
    :aria-valuenow="segment.value ?? undefined"
    :aria-valuetext="ariaValueText"
    :aria-valuemin="segment.min"
    :aria-valuemax="segment.max"
    :style="{ caretColor: 'transparent' }"
    inputmode="numeric"
    autocorrect="off"
    spellcheck="false"
    enterkeyhint="next"
    class="select-none rounded px-1 tabular-nums outline-none focus:bg-blue-500 focus:text-white"
    @focus="handleFocus"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @beforeinput="handleBeforeInput"
    >{{ displayText }}</span
  >
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import { formatMonthName } from "@/lib/time";

import { useSegment } from "./useSegment";

export interface SegmentData {
  type: "day" | "month" | "year";
  value: number | null;
  min: number;
  max: number;
}

const props = defineProps<{
  segment: SegmentData;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  change: [value: number | null];
  increment: [delta: number];
  next: [];
  prev: [];
  clear: [];
}>();

const input = useTemplateRef("input");

const isYear = computed(() => props.segment.type === "year");

const seg = useSegment({
  value: () => props.segment.value,
  min: 1,
  max: computed(() => props.segment.max).value,
  digits: 2,
  disabled: () => props.disabled ?? false,
});

// Year uses its own buffer since it has 4-digit accumulation without auto-advance
const yearBuffer = ref("");

const displayText = computed(() => {
  const { type, value } = props.segment;

  if (value === null) {
    switch (type) {
      case "day": {
        return "dd";
      }
      case "month": {
        return "mm";
      }
      case "year": {
        return "yyyy";
      }
    }
  }

  switch (type) {
    case "day":
    case "month": {
      return value.toString().padStart(2, "0");
    }
    case "year": {
      return value.toString().padStart(4, "0");
    }
  }

  return "";
});

const ariaValueText = computed(() => {
  const { type, value } = props.segment;
  if (value === null) {
    return undefined;
  }

  if (type === "month") {
    return formatMonthName(value);
  }

  return value.toString();
});

const focus = (): void => {
  input.value?.focus();
};

const handleFocus = (): void => {
  seg.resetBuffer();
  yearBuffer.value = "";

  // Collapse selection to hide cursor
  const selection = window.getSelection();
  if (selection && input.value) {
    selection.collapse(input.value);
  }
};

const handleBlur = (): void => {
  seg.resetBuffer();
  yearBuffer.value = "";
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (props.disabled) {
    return;
  }

  switch (event.key) {
    case "ArrowLeft": {
      event.preventDefault();
      emit("prev");

      seg.resetBuffer();
      yearBuffer.value = "";
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      emit("next");

      seg.resetBuffer();
      yearBuffer.value = "";
      break;
    }
    case "ArrowUp": {
      event.preventDefault();
      emit("increment", 1);

      seg.resetBuffer();
      yearBuffer.value = "";
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      emit("increment", -1);

      seg.resetBuffer();
      yearBuffer.value = "";
      break;
    }
    case "Backspace":
    case "Delete": {
      event.preventDefault();
      if (isYear.value) {
        handleYearBackspace();
      } else {
        const result = seg.handleBackspace();
        emit("change", result.value);
        if (result.navigatePrev) {
          emit("prev");
        }
      }
      break;
    }
  }
};

const handleBeforeInput = (event: Event): void => {
  event.preventDefault();

  if (props.disabled) {
    return;
  }

  const ev = event as InputEvent;
  const data = ev.data;
  switch (data) {
    case "0":
    case "1":
    case "2":
    case "3":
    case "4":
    case "5":
    case "6":
    case "7":
    case "8":
    case "9": {
      if (isYear.value) {
        handleYearDigitInput(data);
      } else {
        const result = seg.handleDigitInput(data);
        emit("change", result.value);
        if (result.advance) {
          emit("next");
        }
      }
      break;
    }
  }
};

const handleYearDigitInput = (digit: string): void => {
  const newBuffer = (yearBuffer.value + digit).slice(0, 4);
  yearBuffer.value = newBuffer;
  emit("change", parseInt(newBuffer, 10));

  if (newBuffer.length >= 4) {
    yearBuffer.value = "";
  }
};

const handleYearBackspace = (): void => {
  const { value } = props.segment;

  if (value === null) {
    emit("prev");
    return;
  }

  const displayedStr = value.toString();
  const newStr = displayedStr.slice(0, -1);

  if (newStr.length === 0) {
    emit("change", null);
    yearBuffer.value = "";
  } else {
    const num = parseInt(newStr, 10);
    emit("change", num);
    yearBuffer.value = newStr;
  }
};

defineExpose({
  focus,
  el: input,
});
</script>
